import React from "react";

const TermsAndConditions = () => {
  return (
    <main className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-sky-50 via-blue-50 to-slate-50 px-4 py-8 text-slate-700 sm:px-6 sm:py-12 lg:px-8">
      <header className="mb-8 rounded-2xl bg-gradient-to-r from-blue-950 to-blue-800 px-6 py-8 text-white shadow-md sm:px-9 sm:py-10">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-sky-200">
          Before you book
        </p>
        <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
          Terms & Conditions
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-blue-50 sm:text-base">
          Please review these terms before confirming your booking and share
          the relevant trip information with everyone travelling in your
          group.
        </p>
      </header>

      {/* Payment & Booking Policy */}
      <section className="mb-4 rounded-2xl border border-sky-200 bg-white/90 p-5 shadow-sm sm:p-6">
        <h2 className="mb-3 text-lg font-semibold text-blue-950 sm:text-xl">
          Payment & Booking Policy
        </h2>
        <ul className="list-disc space-y-2 pl-5 text-sm sm:text-base">
          <li>
            Your booking is confirmed only after the required booking payment
            has been received and confirmation has been shared by our team.
          </li>
          <li>
            Pay any outstanding balance to the team on arrival, before
            check-in or the start of your booked experience.
          </li>
          <li>
            If a required payment remains outstanding, the booking or
            participation may be cancelled.
          </li>
          <li>
            Carry sufficient cash for check-in and any on-site expenses,
            especially where digital payment may not be available.
          </li>
          <li>
            Keep a valid government-issued ID available for verification at
            check-in or before departure, as applicable.
          </li>
          <li>
            Booking transfers are not permitted within 48 hours of the
            experience.
            Contact us at least 48 hours in advance if you need to transfer
            your booking to another person.
          </li>
        </ul>
      </section>

      {/* Refund & Responsibility */}
      <section className="mb-4 rounded-2xl border border-sky-200 bg-white/90 p-5 shadow-sm sm:p-6">
        <h2 className="mb-3 text-lg font-semibold text-blue-950 sm:text-xl">
          Refund & Responsibility
        </h2>
        <ul className="list-disc space-y-2 pl-5 text-sm sm:text-base">
          <li>
            Cancellation and refund requests are handled under our separate
            Cancellation Policy. Please review it before booking; services or
            activities not used during a trip are not refundable.
          </li>
          <li>
            Keep your valuables and personal belongings with you. SKAdventures
            is not responsible for personal items that are lost, stolen, or
            damaged.
          </li>
        </ul>
      </section>

      {/* Transportation & Departure */}
      <section className="mb-4 rounded-2xl border border-sky-200 bg-white/90 p-5 shadow-sm sm:p-6">
        <h2 className="mb-3 text-lg font-semibold text-blue-950 sm:text-xl">
          Arrival, Check-in & Departure
        </h2>
        <ul className="list-disc space-y-2 pl-5 text-sm sm:text-base">
          <li>
            Check your booking confirmation for the correct date, meeting
            point or venue, arrival time, and trip contact before you leave.
            Contact our team promptly if any detail is unclear.
          </li>
          <li>
            Arrive at the confirmed meeting point or venue on time and check in
            with the trip team or on-site coordinator. If a departure time is
            provided, report at least 15 minutes in advance.
          </li>
          <li>
            Plan your journey to allow for traffic and delays. The group may
            not be able to wait beyond the scheduled time. Late arrival may
            result in a missed departure or check-in, for which a refund is not
            available.
          </li>
          <li>
            Unless your package specifically includes transportation, arrange
            your own travel to and from the confirmed meeting point or venue.
          </li>
        </ul>
      </section>

      <section className="mb-4 rounded-2xl border border-sky-200 bg-white/90 p-5 shadow-sm sm:p-6">
        <h2 className="mb-3 text-lg font-semibold text-blue-950 sm:text-xl">
          Before You Travel
        </h2>
        <ul className="list-disc space-y-2 pl-5 text-sm sm:text-base">
          <li>
            Check your booking confirmation and make sure the date, number of
            guests, selected package or experience, and contact details are
            correct.
            Tell us promptly about any corrections.
          </li>
          <li>
            Review the itinerary and package inclusions and exclusions so you
            know what is and is not included. Optional services or activities
            may have separate availability and charges.
          </li>
          <li>
            Check the weather forecast and pack clothing, footwear, personal
            medication, and essentials suitable for your booked experience.
          </li>
          <li>
            Tell our team in advance about a relevant medical condition,
            dietary requirement, or assistance need. We will clarify what can
            be accommodated; please do not assume a special arrangement is
            available unless it has been confirmed.
          </li>
          <li>
            Save your booking confirmation and the trip contact details so you
            can reach the team if you have difficulty finding the meeting point
            or venue.
          </li>
        </ul>
      </section>

      {/* Code of Conduct */}
      <section className="mb-4 rounded-2xl border border-sky-200 bg-white/90 p-5 shadow-sm sm:p-6">
        <h2 className="mb-3 text-lg font-semibold text-blue-950 sm:text-xl">
          Conduct & On-site Guidelines
        </h2>
        <ul className="list-disc space-y-2 pl-5 text-sm sm:text-base">
          <li>
            Follow venue rules and applicable local laws regarding smoking and
            alcohol. Where alcohol is permitted, comply with any permit
            requirements and consume responsibly. Do not participate in an
            activity while under the influence of alcohol or drugs.
          </li>
          <li>
            Choose an experience appropriate for your health and abilities.
            Activities may involve physical effort and changing outdoor
            conditions.
          </li>
          <li>
            Follow instructions from the trip team, guides, venue staff, and
            activity operators. Treat other guests respectfully and do not
            behave in a way that disturbs or endangers anyone. Serious or
            repeated misconduct may result in removal from the experience
            without a refund.
          </li>
          <li>
            Stay with your group where the experience is group-led, and inform
            the designated team member before leaving the group or assigned
            area.
          </li>
          <li>
            Respect the venue and natural surroundings. Dispose of waste
            responsibly, avoid disturbing wildlife or plants, and follow any
            site-specific safety instructions.
          </li>
          <li>
            You are responsible for damage to venue or activity property
            caused by you or your group. Repair or replacement charges may
            apply.
          </li>
        </ul>
      </section>

      {/* Risk & Liability */}
      <section className="mb-4 rounded-2xl border border-sky-200 bg-white/90 p-5 shadow-sm sm:p-6">
        <h2 className="mb-3 text-lg font-semibold text-blue-950 sm:text-xl">
          Risk & Liability
        </h2>
        <p className="text-sm sm:text-base">
          Travel and recreational experiences may involve inherent risks,
          including changing conditions, accidents, injury, or property
          damage. Follow the trip team's safety guidance and make responsible
          choices. Services and activities may be affected by weather, safety
          conditions, government action, or other circumstances beyond our
          control. Nothing in these terms is intended to limit rights that
          cannot be excluded under applicable law.
        </p>
      </section>

      {/* Force Majeure & Itinerary Changes */}
      <section className="mb-4 rounded-2xl border border-sky-200 bg-white/90 p-5 shadow-sm sm:p-6">
        <h2 className="mb-3 text-lg font-semibold text-blue-950 sm:text-xl">
          Force Majeure & Itinerary Changes
        </h2>
        <ul className="list-disc space-y-2 pl-5 text-sm sm:text-base">
          <li>
            Weather, natural hazards, roadblocks, government restrictions,
            political disturbances, or other unforeseen circumstances may
            cause delays, service changes, or itinerary adjustments.
          </li>
          <li>
            Follow any changes communicated by the trip team. Services or
            activities may be changed, delayed, or unavailable when conditions
            make them unsafe or impractical.
          </li>
          <li>
            Travel or personal accident insurance is not included unless your
            package explicitly says otherwise. Consider arranging suitable
            insurance before travel.
          </li>
        </ul>
      </section>

      {/* Health Declaration */}
      <section className="mb-4 rounded-2xl border border-sky-200 bg-white/90 p-5 shadow-sm sm:p-6">
        <h2 className="mb-3 text-lg font-semibold text-blue-950 sm:text-xl">
          📝 Health Declaration
        </h2>
        <ul className="list-disc space-y-2 pl-5 text-sm sm:text-base">
          <li>
            Consider whether you are physically and mentally able to take part
            in the experience you have booked.
          </li>
          <li>
            Inform the trip team in advance about any medical condition that
            could affect your participation. If you are unsure whether an
            activity is suitable for you, consult a medical professional
            before travel.
          </li>
          <li>
            Do not take part in any activity while under the influence of
            alcohol or drugs.
          </li>
        </ul>
      </section>

      {/* Acknowledgement of Risk */}
      <section className="mb-4 rounded-2xl border border-sky-200 bg-white/90 p-5 shadow-sm sm:p-6">
        <h2 className="mb-3 text-lg font-semibold text-blue-950 sm:text-xl">
          Acknowledgement of Risk & Consent
        </h2>
        <ul className="list-disc space-y-2 pl-5 text-sm sm:text-base">
          <li>
            By booking, participants acknowledge that travel and recreational
            activities may involve risks such as falls, injuries, changing
            conditions, and interactions with the natural environment.
          </li>
          <li>
            Participants are responsible for their own conduct and for
            following the instructions of the trip team, guides, venue staff,
            and activity operators.
          </li>
          <li>
            Read the booking information and ask the team about any part of the
            experience or package that is unclear before booking.
          </li>
          <li>
            Nothing in this acknowledgement is intended to waive rights that
            cannot legally be waived.
          </li>
        </ul>
      </section>

      {/* Media Usage Policy */}
      <section className="mb-4 rounded-2xl border border-sky-200 bg-white/90 p-5 shadow-sm sm:p-6">
        <h2 className="mb-3 text-lg font-semibold text-blue-950 sm:text-xl">
          Media Usage Policy
        </h2>
        <p className="text-sm sm:text-base">
          SKAdventures may capture photos or videos during trips for
          promotional use. Please let the trip team know before the activity if
          you have a concern about being photographed or recorded. Participants
          retain responsibility for content they create and should get
          permission before commercially using SKAdventures’ branded materials.
        </p>
      </section>

      {/* Footer Note */}
      <div className="rounded-xl border border-sky-200 bg-sky-100/70 px-5 py-4 text-sm leading-6 text-blue-950">
        <p>
          These terms should be read together with the separate Cancellation
          Policy and the booking information provided for your experience.
          Contact SKAdventures before departure if you need clarification
          about your booking or preparation.
        </p>
      </div>
    </main>
  );
};

export default TermsAndConditions;
